import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import useCreateTicket from '../useCreateTicket';
import useUpdateTicket from '../useUpdateTicket';

import useZDUser from 'services/help-center/queries/useZDUser';
import useStatsigGate from 'modules/common/useStatsigGate';
import { FeatureGates } from 'modules/common/constants/featuregates';
import { insertTicketIntoLists, rememberCreatedTicket } from 'services/help-center/recentlyCreatedTickets';
import { Ticket } from 'modules/IMC/MessageCenter/components/MessageCenterContent/MessageList/MessageList.interface';
import {
    BOT_AUTHOR_ID,
    BOT_CUSTOM_FIELD_ID,
    BOT_CUSTOM_FIELD_VALUES,
    LAST_VIEWED_FIELD_ID,
} from 'services/help-center/constants';

const DEFAULT_MESSAGE = "Your message is with our team. We'll get back to you within a business day.";

interface SynthesizedComment {
    authorId: number;
    plainBody: string;
    htmlBody: string;
    createdAt?: string;
}

// Zendesk never returns `last_comment` on a write, and the list row needs it for its preview, date,
// sort and unread state, so it is built from what was just sent (as useSendComment does).
function toListTicket(ticket: Ticket, { authorId, plainBody, htmlBody, createdAt }: SynthesizedComment): Ticket {
    return {
        ...ticket,
        last_comment: {
            id: 0,
            type: 'Comment',
            author_id: authorId,
            body: plainBody,
            html_body: htmlBody,
            plain_body: plainBody,
            public: true,
            audit_id: 0,
            attachments: [],
            via: { channel: 'api', source: { from: {}, to: {}, rel: null } },
            created_at: createdAt ?? new Date().toISOString(),
        },
    };
}

export default function useNewMessage() {
    const isZendeskMemberProjectionEnabled = useStatsigGate(FeatureGates.ZendeskMemberProjection);
    const { data: zdUser } = useZDUser();
    const queryClient = useQueryClient();
    const requesterId = zdUser?.id;

    const { mutateAsync: createTicket } = useCreateTicket();
    const { mutateAsync: updateTicket } = useUpdateTicket();

    return async (
        subject: string,
        description: string,
        files: string[],
        tags?: string[],
        customMessage?: string,
        recipient?: string
    ) => {
        if (!requesterId) {
            // Throw an axios-like error so existing error handling shows a friendly message
            throw {
                isAxiosError: true,
                response: { status: 403 },
                message: 'Session expired',
            } as any;
        }

        const ticket = await createTicket({
            ticket: {
                subject,
                requester_id: requesterId,
                comment: {
                    body: description,
                    uploads: files,
                },
                status: 'pending',
                tags: [...(tags || []), 'origin:IMC'],
                ...(recipient && { recipient }),
            },
        });

        // Add 2 seconds to the timestamp in case the bot message is created_at date is after the last_viewed timestamp
        const updatedDateTimestamp = dayjs().utc().add(2, 'seconds').format('YYYY-MM-DDTHH:mm:ss[Z]');
        const lastViewedField = { id: LAST_VIEWED_FIELD_ID, value: updatedDateTimestamp };

        const messageText = customMessage?.trim() || DEFAULT_MESSAGE;

        const botHtmlBody = `<div>
                                <p>
                                    ${messageText}
                                </p>
                            </div>`;

        const initialMessage = {
            ticket: {
                comment: {
                    author_id: BOT_AUTHOR_ID,
                    html_body: botHtmlBody,
                },
                status: 'open',
                custom_fields: [
                    { id: BOT_CUSTOM_FIELD_ID, value: BOT_CUSTOM_FIELD_VALUES.NO_SUGGESTIONS },
                    lastViewedField,
                ],
            },
        };

        if (!isZendeskMemberProjectionEnabled) {
            const { ticket: updatedTicket } = await updateTicket({
                ticketId: ticket.id,
                payload: initialMessage,
            });

            queryClient.invalidateQueries({
                queryKey: ['messageCenterTickets'],
            });

            return updatedTicket;
        }

        let listTicket: Ticket;
        let createdTicket: Ticket;
        try {
            const { ticket: updatedTicket } = await updateTicket({
                ticketId: ticket.id,
                payload: initialMessage,
            });
            createdTicket = updatedTicket ?? ticket;
            listTicket = toListTicket(
                { ...ticket, ...updatedTicket },
                {
                    authorId: BOT_AUTHOR_ID,
                    plainBody: messageText,
                    htmlBody: botHtmlBody,
                    createdAt: updatedTicket?.updated_at,
                }
            );
        } catch {
            // The ticket exists, so failing here would invite a retry that creates a duplicate. The
            // member's own message is then the latest comment, not the Concierge one. The failed PUT
            // is already reported by updateTicket's request, as 'updateTicket', the same as on main.
            createdTicket = ticket;
            listTicket = toListTicket(ticket, {
                authorId: requesterId,
                plainBody: description,
                htmlBody: description,
                createdAt: ticket.created_at,
            });
        }

        rememberCreatedTicket(queryClient, requesterId, listTicket);
        insertTicketIntoLists(queryClient, requesterId, listTicket);

        queryClient.invalidateQueries({
            queryKey: ['messageCenterTickets'],
        });

        return createdTicket;
    };
}
